import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  Typography,
  Box
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';

/**
 * EvidenceAccordion Component
 * Display evidence references with expandable details
 */

interface EvidenceRef {
  refType: "CGM_EVENT" | "MEAL_ENTRY" | "CONTEXT_NOTE" 
         | "LAB_RESULT" | "PROTOCOL_VERSION" | "MESSAGE_RESPONSE";
  refId: string;
  label?: string;
  meta?: Record<string, unknown>;
}

interface EvidenceAccordionProps {
  evidenceRefs: EvidenceRef[];
  onEvidenceClick?: (ref: EvidenceRef) => void;
  defaultExpanded?: boolean;
}

export const EvidenceAccordion: React.FC<EvidenceAccordionProps> = ({
  evidenceRefs,
  onEvidenceClick,
  defaultExpanded = false
}) => {
  const getTypeColor = (type: string) => {
    const colors: Record<string, "default" | "primary" | "secondary" | "success" | "error" | "info" | "warning"> = {
      CGM_EVENT: 'primary',
      MEAL_ENTRY: 'success',
      CONTEXT_NOTE: 'info',
      LAB_RESULT: 'secondary',
      PROTOCOL_VERSION: 'warning',
      MESSAGE_RESPONSE: 'default',
    };
    return colors[type] || 'default';
  };

  return (
    <Accordion defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LinkIcon fontSize="small" />
          <Typography>
            Evidence ({evidenceRefs.length} {evidenceRefs.length === 1 ? 'reference' : 'references'})
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <List dense>
          {evidenceRefs.map((ref, index) => (
            <ListItem key={index} disablePadding>
              <ListItemButton
                onClick={() => onEvidenceClick?.(ref)}
                disabled={!onEvidenceClick}
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip
                        label={ref.refType}
                        color={getTypeColor(ref.refType)}
                        size="small"
                      />
                      <Typography variant="body2">
                        {ref.label || ref.refId}
                      </Typography>
                    </Box>
                  }
                  secondary={ref.meta ? JSON.stringify(ref.meta) : undefined}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};
